import { useState, type ReactNode } from "react";
import {
  Check,
  CheckCircle2,
  ChevronDown,
  CircleMinus,
  CirclePlus,
  EyeOff,
  HelpCircle,
  Loader2,
  Minus,
  Plus,
  ScanSearch,
  Scissors,
  X,
  XCircle,
} from "lucide-react";
import type { EvaluationMetadata } from "@/api/pizza-checker/schemas";
import type { ExtraIngredient, ExtraIngredientsEvaluation, PizzaEvaluationMetadata, PizzaSaverPlate } from "@/api/custom-schema";
import { usePizzaEvaluationContext, type HoveredHeatmap } from "@/context/pizza-evaluation-context";
import { useHoverHighlight } from "@/hooks/use-hover-highlight";
import { usePizzaObstacles } from "@/hooks/use-pizza-obstacles";
import { storedObstacleCheck } from "@/lib/obstacles";
import { photoQualityReason, pizzaVerdict, type PizzaVerdict } from "@/lib/pizza-verdict";
import { cn } from "@/lib/utils";
import { DonenessMiniGauge, ThresholdBar } from "@/components/evaluation/doneness-gauge";
import { PhotoQualityChecks } from "@/orders/components/evaluation";
import { useLocalization } from "@/context/localization-context";

export type ViewerTab = "original" | "masked" | "evaluation" | "slicing" | "obstacles";

type RowState = "ok" | "nok" | "info";

const capitalizeList = (names: string[] | undefined | null) =>
  (names ?? []).map((name) => name.charAt(0).toUpperCase() + name.slice(1)).join(", ");

/**
 * Headline of the panel. The only strongly coloured element: green for a pass, red (with an accent edge)
 * for a failure; what failed is listed by the red rows right below it.
 */
function VerdictBlock({ verdict, loading }: { verdict: PizzaVerdict; loading?: boolean }) {
  const { t, tp } = useLocalization();
  const failed = verdict.checks.filter((check) => check.status === "NOK").length;
  const pending = loading && verdict.kind === "not_evaluated";
  const nok = verdict.kind === "nok" || verdict.kind === "no_pizza" || verdict.kind === "invalid_photo";

  const subtitle = pending
    ? t("detail.panel.loading")
    : verdict.kind === "ok"
      ? tp("detail.panel.allPassed", verdict.checks.length)
      : verdict.kind === "nok"
        ? tp("detail.panel.failed", verdict.checks.length, { failed })
        : verdict.kind === "not_evaluated"
          ? t("detail.panel.noEvaluation")
          : verdict.reasons[0];

  return (
    <div
      className={cn(
        "flex items-center gap-3 border-b px-4 py-3",
        nok && "bg-red-500/[0.06] shadow-[inset_3px_0_0_theme(colors.red.500)]",
      )}
    >
      {pending ? (
        <Loader2 className="h-7 w-7 shrink-0 animate-spin text-muted-foreground" />
      ) : nok ? (
        <XCircle className="h-7 w-7 shrink-0 text-red-400" />
      ) : verdict.kind === "ok" ? (
        <CheckCircle2 className="h-7 w-7 shrink-0 text-green-400" />
      ) : (
        <HelpCircle className="h-7 w-7 shrink-0 text-muted-foreground" />
      )}
      <div className="min-w-0">
        <div
          className={cn(
            "text-lg font-semibold leading-tight",
            nok ? "text-red-400" : verdict.kind === "ok" ? "text-green-400" : "text-foreground",
          )}
        >
          {pending ? t("detail.evaluating") : verdict.title}
        </div>
        <div className="truncate text-xs text-muted-foreground">{subtitle}</div>
      </div>
    </div>
  );
}

/**
 * One check of the verdict as a list row: icon, name and a one-line summary. Passed checks stay grey and
 * folded, failed ones are red and open; a row the user opens or closes keeps that while browsing.
 */
function CheckRow({
  id,
  title,
  state,
  summary,
  icon,
  expanded,
  onToggle,
  onActivate,
  highlight,
  children,
}: {
  id: string;
  title: string;
  state: RowState;
  summary?: ReactNode;
  icon?: ReactNode;
  expanded: boolean;
  onToggle: (id: string) => void;
  /** a row without details does this instead (open a viewer layer) */
  onActivate?: () => void;
  /** hover / tap highlight on the photo for a row without details */
  highlight?: Record<string, unknown>;
  children?: ReactNode;
}) {
  const hasDetails = !!children;
  // the status mark is the one coloured thing on a row: green tick / red cross in a soft disc
  const Icon =
    icon ??
    (state === "nok" ? (
      <X className="h-3.5 w-3.5" strokeWidth={3} />
    ) : state === "ok" ? (
      <Check className="h-3.5 w-3.5" strokeWidth={3} />
    ) : null);

  return (
    <div className="border-b border-border/60 last:border-b-0">
      <button
        type="button"
        aria-expanded={hasDetails ? expanded : undefined}
        {...highlight}
        // a tap both highlights on the photo (touch) and opens the row's details
        onClick={() => {
          (highlight?.onClick as (() => void) | undefined)?.();
          if (hasDetails) onToggle(id);
          else onActivate?.();
        }}
        className={cn(
          "flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors",
          (hasDetails || onActivate || highlight) && "hover:bg-muted/40",
          "data-[highlighted]:bg-muted/60",
        )}
      >
        <span
          className={cn(
            "flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
            state === "nok" && "bg-red-500/15 text-red-400 ring-1 ring-inset ring-red-500/30",
            state === "ok" && "bg-green-500/10 text-green-400",
            state === "info" && "bg-muted/60 text-muted-foreground",
          )}
        >
          {Icon}
        </span>
        <span
          className={cn(
            "shrink-0 text-sm",
            state === "nok" ? "font-medium text-foreground" : "text-foreground/80",
          )}
        >
          {title}
        </span>
        <span
          className={cn(
            "ml-auto min-w-0 truncate pl-2 text-right text-xs",
            state === "nok" ? "font-medium text-red-300" : "text-muted-foreground",
          )}
          title={typeof summary === "string" ? summary : undefined}
        >
          {summary}
        </span>
        {hasDetails && (
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 text-muted-foreground/50 transition-transform duration-200",
              expanded && "rotate-180",
            )}
          />
        )}
      </button>
      {hasDetails && expanded && (
        <div className="px-4 pb-2.5">
          <div className="rounded-lg bg-muted/25 px-3 py-2.5">{children}</div>
        </div>
      )}
    </div>
  );
}

type ChipState = "found" | "missing" | "unexpected" | "non_detectable";

const CHIP_STATES: ChipState[] = ["found", "missing", "unexpected", "non_detectable"];

/** the mark of a chip state, also used by the legend under the chips */
function ChipStateIcon({ state }: { state: ChipState }) {
  if (state === "found") return <Check className="h-3 w-3 shrink-0 text-green-400" />;
  if (state === "missing") return <X className="h-3 w-3 shrink-0" />;
  if (state === "unexpected") return <Plus className="h-3 w-3 shrink-0" />;
  return <EyeOff className="h-3 w-3 shrink-0" />;
}

/**
 * An ingredient: hover (tap on touch screens) highlights its detections on the photo. A detected one is
 * tinted green, a missing one red, one beyond the order orange, a non-detectable one is dashed and grey.
 */
function ComponentChip({
  name,
  state,
  prefix,
  suffix,
  title,
  highlight,
}: {
  name: string;
  state: ChipState;
  prefix?: ReactNode;
  suffix?: string;
  /** tooltip instead of the state's own (removals) */
  title?: string;
  highlight: Record<string, unknown>;
}) {
  const { t } = useLocalization();
  return (
    <button
      type="button"
      {...highlight}
      title={
        title ??
        (state === "non_detectable"
          ? t("detail.chip.notDetectableTitle")
          : state === "missing"
            ? t("detail.chip.missingTitle")
            : t("detail.chip.detectedTitle"))
      }
      className={cn(
        "inline-flex h-7 items-center gap-1 rounded-md border px-2 text-xs transition-colors",
        state === "found" && "border-green-500/30 bg-green-500/10 text-green-300 hover:bg-green-500/15",
        state === "missing" && "border-red-500/40 bg-red-500/10 font-medium text-red-300 hover:bg-red-500/15",
        state === "unexpected" &&
          "border-orange-500/40 bg-orange-500/10 font-medium text-orange-300 hover:bg-orange-500/15",
        state === "non_detectable" &&
          "border-dashed border-muted-foreground/30 bg-transparent text-muted-foreground hover:text-foreground",
        "data-[highlighted]:ring-2 data-[highlighted]:ring-primary/60",
      )}
    >
      {prefix}
      <ChipStateIcon state={state} />
      <span className="capitalize">{name}</span>
      {suffix && <span className="opacity-70">{suffix}</span>}
    </button>
  );
}

/**
 * An ingredient the pizza has beyond its recipe and the order (a swap, something the kitchen added): an
 * orange chip, its pieces or sauce share (from the extra ingredients evaluation) only in the tooltip.
 */
function UnexpectedChip({
  name,
  item,
  highlight,
}: {
  name: string;
  item: ExtraIngredient | undefined;
  highlight: Record<string, unknown>;
}) {
  const { t, formatNumber } = useLocalization();
  const sauce = typeof item?.share === "number";
  const share = sauce ? formatNumber(item?.share as number, { style: "percent", maximumFractionDigits: 0 }) : "";
  const count = typeof item?.count === "number" ? item.count : null;
  return (
    <ComponentChip
      name={name}
      state="unexpected"
      title={
        sauce
          ? t("detail.extra.sauceTitle", { share })
          : count !== null
            ? t("detail.extra.pieceTitle", { count })
            : t("detail.chip.unexpectedTitle")
      }
      highlight={highlight}
    />
  );
}

/** The states that occur among the given chip lists, in legend order. */
const presentStates = (lists: Partial<Record<ChipState, unknown[] | null | undefined>>) =>
  CHIP_STATES.filter((state) => (lists[state]?.length ?? 0) > 0);

/**
 * Chips with a one-line legend under them (only the states that occur in the list). `labels` renames a
 * state where it means something else (removals: "removed" / "still on the pizza").
 */
function ChipList({
  children,
  states = [],
  labels,
}: {
  children: ReactNode;
  states?: ChipState[];
  labels?: Partial<Record<ChipState, string>>;
}) {
  const { t } = useLocalization();
  const defaultLabels: Record<ChipState, string> = {
    found: t("detail.chip.detected"),
    missing: t("detail.chip.missing"),
    unexpected: t("detail.chip.unexpected"),
    non_detectable: t("detail.chip.notDetectable"),
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
      {states.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-muted-foreground">
          {states.map((state) => (
            <span key={state} className="inline-flex items-center gap-1">
              <span
                className={cn(state === "missing" && "text-red-400", state === "unexpected" && "text-orange-400")}
              >
                <ChipStateIcon state={state} />
              </span>
              {labels?.[state] ?? defaultLabels[state]}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

interface EvaluationPanelProps {
  metadata: EvaluationMetadata | null | undefined;
  loading?: boolean;
  isDeveloperMode?: boolean;
  /** opens a layer of the photo viewer (slicing, obstacles) */
  onOpenTab?: (tab: ViewerTab) => void;
  className?: string;
}

/**
 * The evaluation of one pizza in a single panel: the verdict, then every check it is made of as a row
 * (failed rows open with what failed, ingredients as chips that highlight on the photo, doneness and
 * photo quality as probability bars against their thresholds).
 */
export function EvaluationPanel({
  metadata,
  loading,
  isDeveloperMode = false,
  onOpenTab,
  className,
}: EvaluationPanelProps) {
  const { t, formatNumber } = useLocalization();
  const {
    hoveredClass,
    setHoveredClass,
    hoveredHeatmap,
    setHoveredHeatmap,
    selectedPizzaId,
  } = usePizzaEvaluationContext();
  const classHighlight = useHoverHighlight<string>(hoveredClass, setHoveredClass);
  const heatmapHighlight = useHoverHighlight<HoveredHeatmap>(hoveredHeatmap, setHoveredHeatmap);
  // the on-demand obstacle check of an evaluation made before the segmentation (read from the query cache)
  const { obstacles, preview: obstaclesPreview } = usePizzaObstacles(
    selectedPizzaId,
    storedObstacleCheck(metadata),
    false,
  );
  // rows the user opened / closed, kept while browsing pizzas; the others follow their verdict
  const [toggled, setToggled] = useState<Record<string, boolean>>({});

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const meta = metadata as Record<string, any> | null | undefined;
  const verdict = pizzaVerdict(meta);

  if (!meta) {
    return (
      <section className={cn("overflow-hidden rounded-xl border bg-card", className)}>
        <VerdictBlock verdict={verdict} loading={loading} />
        {loading && (
          <div className="space-y-2 px-4 py-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-6 animate-pulse rounded bg-muted/60" />
            ))}
          </div>
        )}
      </section>
    );
  }

  const pct = (value: number) =>
    formatNumber(value, { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const row = (id: string, state: RowState) => ({
    id,
    state,
    expanded: toggled[id] ?? state === "nok",
    onToggle: (key: string) =>
      setToggled((prev) => ({ ...prev, [key]: !(prev[key] ?? state === "nok") })),
  });

  const components = meta.components_evaluation;
  const base = components?.base_components;
  const extras = components?.extras;
  const removals = components?.removals;
  const showExtras = !!(extras?.expected?.length || extras?.non_detectable?.length);
  const showRemovals = !!(removals?.found?.length || removals?.not_expected?.length);
  // what the pizza has beyond its recipe and the order (missing on evaluations before 2026-10-31): fails
  // the ingredients; the extra ingredients evaluation holds the pieces / sauce share of each
  const extra = meta.extra_ingredients_evaluation as ExtraIngredientsEvaluation | null | undefined;
  const extraFound = extra?.enabled ? extra.found ?? [] : [];
  const extraIgnored = extra?.enabled ? extra.ignored ?? [] : [];
  const unexpected: string[] = base?.unexpected ?? extraFound.map((item) => item.name);
  const extraOf = (name: string) => extraFound.find((item) => item.name === name);
  const doneness = meta.doneness_evaluation;
  const burntModel = meta.burnt_model;
  const photo = meta.photo_quality_evaluation;
  const slicing = meta.slicing_model;
  const obstacleCheck = photo?.checks?.obstacle_in_pizza;
  const shortCircuited = verdict.kind === "no_pizza" || verdict.kind === "invalid_photo";

  const burntProbability = doneness?.burnt?.probability ?? burntModel?.probability;
  const rawEvaluated = !!doneness?.raw && doneness.raw.evaluated !== false;
  const rawProbability = doneness?.raw?.probability ?? burntModel?.raw_probability;
  const burntNok = !!doneness?.burnt?.status;
  const rawNok = rawEvaluated && !!doneness?.raw?.status;
  const hasProbability = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value);
  // older evaluations carry only { status }
  const saver = meta.pizza_saver_evaluation as PizzaEvaluationMetadata["pizza_saver_evaluation"] | undefined;
  const saverWaived = !!saver && saver.required === false && saver.waived_by === "plate";
  const saverPlate: PizzaSaverPlate | null = saver?.plate ?? null;

  // only counts next to the name, the chips name the ingredients
  const baseSummary =
    [
      base?.missing?.length ? t("detail.panel.missingCount", { count: base.missing.length }) : null,
      unexpected.length ? t("detail.panel.unexpectedCount", { count: unexpected.length }) : null,
      base?.found?.length ? t("detail.panel.foundCount", { count: base.found.length }) : null,
      base?.non_detectable?.length
        ? t("detail.panel.notDetectableCount", { count: base.non_detectable.length })
        : null,
    ]
      .filter(Boolean)
      .join(" · ") || t("status.ok");

  const donenessSummary = [
    doneness?.burnt !== undefined
      ? t("detail.panel.burntValue", {
          value: hasProbability(burntProbability)
            ? pct(burntProbability)
            : burntNok
              ? t("detail.panel.yes")
              : t("detail.panel.no"),
        })
      : null,
    rawEvaluated
      ? t("detail.panel.rawValue", {
          value: hasProbability(rawProbability)
            ? pct(rawProbability)
            : rawNok
              ? t("detail.panel.yes")
              : t("detail.panel.no"),
        })
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const slicingSmall = slicing?.small_count ?? 0;

  return (
    <section
      className={cn("overflow-hidden rounded-xl border bg-card", className)}
      aria-label={t("detail.panel.title")}
    >
      <VerdictBlock verdict={verdict} loading={loading} />
      <div>
        {photo && (
          <CheckRow
            {...row("photo", photo.status === "NOK" ? "nok" : "ok")}
            title={t("verdict.check.photo")}
            summary={photo.status === "NOK" ? photoQualityReason(photo) : t("status.ok")}
          >
            <PhotoQualityChecks
              metadata={meta}
              obstaclePreview={obstaclesPreview ? obstacles : null}
            />
            {obstacleCheck && onOpenTab && (
              <button
                type="button"
                onClick={() => onOpenTab("obstacles")}
                className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                <ScanSearch className="h-3.5 w-3.5" />
                {t("detail.panel.showObstacleLayer")}
              </button>
            )}
          </CheckRow>
        )}

        {!shortCircuited && base && (
          <CheckRow
            {...row("base", base.status === "NOK" ? "nok" : "ok")}
            title={t("verdict.check.base")}
            summary={baseSummary}
          >
            <ChipList
              states={presentStates({
                found: base.found,
                missing: base.missing,
                unexpected,
                non_detectable: base.non_detectable,
              })}
            >
              {(base.missing ?? []).map((name: string) => (
                <ComponentChip key={`m-${name}`} name={name} state="missing" highlight={classHighlight(name)} />
              ))}
              {unexpected.map((name) => (
                <UnexpectedChip key={`u-${name}`} name={name} item={extraOf(name)} highlight={classHighlight(name)} />
              ))}
              {(base.found ?? []).map((name: string) => (
                <ComponentChip key={`f-${name}`} name={name} state="found" highlight={classHighlight(name)} />
              ))}
              {(base.non_detectable ?? []).map((name: string) => (
                <ComponentChip
                  key={`n-${name}`}
                  name={name}
                  state="non_detectable"
                  highlight={classHighlight(name)}
                />
              ))}
              {!base.found?.length && !base.missing?.length && !unexpected.length && !base.non_detectable?.length && (
                <span className="text-xs text-muted-foreground">{t("detail.panel.noIngredients")}</span>
              )}
            </ChipList>
            {isDeveloperMode && extra && (
              <div className="mt-2 flex flex-col gap-1 text-[11px] text-muted-foreground">
                {!extra.enabled ? (
                  <p>{t("detail.extra.off")}</p>
                ) : (
                  typeof extra.min_count === "number" &&
                  typeof extra.min_confidence === "number" && (
                    <p>{t("detail.extra.rule", { count: extra.min_count, confidence: pct(extra.min_confidence) })}</p>
                  )
                )}
                {extraIgnored.length > 0 && (
                  <p>
                    {t("detail.extra.ignored", {
                      list: extraIgnored
                        .map((item) => (typeof item.count === "number" ? `${item.name} (${item.count})` : item.name))
                        .join(", "),
                    })}
                  </p>
                )}
              </div>
            )}
          </CheckRow>
        )}

        {!shortCircuited && showExtras && (
          <CheckRow
            {...row("extras", extras.status === "NOK" ? "nok" : "ok")}
            title={t("verdict.check.extras")}
            summary={
              extras.missing?.length
                ? t("verdict.reason.missing", { list: capitalizeList(extras.missing) })
                : capitalizeList([...(extras.found ?? []), ...(extras.non_detectable ?? [])])
            }
          >
            <ChipList
              states={presentStates({
                found: extras.found,
                missing: extras.missing,
                non_detectable: extras.non_detectable,
              })}
            >
              {(extras.missing ?? []).map((name: string) => (
                <ComponentChip
                  key={`m-${name}`}
                  name={name}
                  state="missing"
                  prefix={<CirclePlus className="h-3 w-3" />}
                  highlight={classHighlight(name)}
                />
              ))}
              {(extras.found ?? []).map((name: string) => (
                <ComponentChip
                  key={`f-${name}`}
                  name={name}
                  state="found"
                  prefix={<CirclePlus className="h-3 w-3 opacity-60" />}
                  highlight={classHighlight(name)}
                />
              ))}
              {(extras.non_detectable ?? []).map((name: string) => (
                <ComponentChip
                  key={`n-${name}`}
                  name={name}
                  state="non_detectable"
                  prefix={<CirclePlus className="h-3 w-3" />}
                  highlight={classHighlight(name)}
                />
              ))}
            </ChipList>
          </CheckRow>
        )}

        {!shortCircuited && showRemovals && (
          <CheckRow
            {...row("removals", removals.status === "NOK" ? "nok" : "ok")}
            title={t("verdict.check.removals")}
            summary={
              removals.found?.length
                ? t("detail.panel.stillOnList", { list: capitalizeList(removals.found) })
                : t("detail.panel.removedList", { list: capitalizeList(removals.not_expected) })
            }
          >
            <ChipList
              states={presentStates({ found: removals.not_expected, missing: removals.found })}
              labels={{ found: t("detail.chip.legendRemoved"), missing: t("detail.chip.legendStillOn") }}
            >
              {(removals.found ?? []).map((name: string) => (
                <ComponentChip
                  key={`f-${name}`}
                  name={name}
                  state="missing"
                  prefix={<CircleMinus className="h-3 w-3" />}
                  suffix={t("detail.chip.stillOn")}
                  title={t("detail.chip.stillOnTitle")}
                  highlight={classHighlight(name)}
                />
              ))}
              {(removals.not_expected ?? []).map((name: string) => (
                <ComponentChip
                  key={`r-${name}`}
                  name={name}
                  state="found"
                  prefix={<CircleMinus className="h-3 w-3 opacity-60" />}
                  suffix={t("detail.chip.removed")}
                  title={t("detail.chip.removedTitle")}
                  highlight={classHighlight(name)}
                />
              ))}
            </ChipList>
          </CheckRow>
        )}

        {!shortCircuited && doneness && (
          <CheckRow
            {...row("doneness", burntNok || rawNok ? "nok" : "ok")}
            title={t("detail.doneness")}
            summary={
              burntNok && rawNok
                ? t("detail.panel.burntAndRaw")
                : burntNok
                  ? hasProbability(burntProbability)
                    ? t("detail.panel.burntValue", { value: pct(burntProbability) })
                    : t("verdict.check.burnt")
                  : rawNok
                    ? hasProbability(rawProbability)
                      ? t("detail.panel.rawValue", { value: pct(rawProbability) })
                      : t("verdict.check.raw")
                    : donenessSummary
            }
          >
            <div className="flex flex-col gap-3">
              {doneness.burnt !== undefined &&
                (hasProbability(burntProbability) ? (
                  <div
                    {...heatmapHighlight("burnt")}
                    className="-mx-1.5 rounded-md px-1.5 py-1 data-[highlighted]:bg-muted/50"
                  >
                    <DonenessMiniGauge
                      label={t("verdict.check.burnt")}
                      probability={burntProbability}
                      threshold={burntModel?.threshold ?? 0.5}
                      positive={burntNok}
                      tone="burnt"
                    />
                  </div>
                ) : (
                  <LegacyDonenessRow
                    label={t("verdict.check.burnt")}
                    positive={burntNok}
                    confidence={doneness.burnt.confidence}
                  />
                ))}
              {rawEvaluated &&
                (hasProbability(rawProbability) ? (
                  <div
                    {...heatmapHighlight("raw")}
                    className="-mx-1.5 rounded-md px-1.5 py-1 data-[highlighted]:bg-muted/50"
                  >
                    <DonenessMiniGauge
                      label={t("verdict.check.raw")}
                      probability={rawProbability}
                      threshold={burntModel?.raw_threshold ?? 0.5}
                      positive={rawNok}
                      tone="raw"
                    />
                  </div>
                ) : (
                  <LegacyDonenessRow
                    label={t("verdict.check.raw")}
                    positive={rawNok}
                    confidence={doneness.raw.confidence}
                  />
                ))}
              {isDeveloperMode && burntModel && (
                <div className="text-[11px] text-muted-foreground">
                  <span className="font-mono">
                    {burntModel.name} {burntModel.version}
                  </span>{" "}
                  ·{" "}
                  {t("detail.gauge.threshold", {
                    value: formatNumber(burntModel.threshold, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }),
                  })}
                  {doneness.raw?.evaluated === false && ` · ${t("detail.panel.rawNotEvaluated")}`}
                </div>
              )}
            </div>
          </CheckRow>
        )}

        {!shortCircuited && saver && (
          <CheckRow
            {...row("saver", saverWaived ? "info" : saver.status === "NOK" ? "nok" : "ok")}
            title={t("verdict.check.saver")}
            // waived by the plate rule: not a pass of the check, so calm grey instead of the green tick
            icon={saverWaived ? <Minus className="h-3.5 w-3.5" strokeWidth={3} /> : undefined}
            summary={
              saverWaived
                ? t("detail.panel.saverWaived")
                : saver.status === "NOK"
                  ? t("detail.panel.saverMissing")
                  : t("detail.panel.saverPresent")
            }
            highlight={classHighlight("red pizza saver")}
          >
            {isDeveloperMode && saverPlate && hasProbability(saverPlate.share) && (
              <div className="flex flex-col gap-1.5">
                <div className="flex items-baseline justify-between gap-3 text-xs">
                  <span className="text-muted-foreground">{t("detail.panel.plateShare")}</span>
                  <span className="tabular-nums text-foreground/90">{pct(saverPlate.share)}</span>
                </div>
                {hasProbability(saverPlate.threshold) && (
                  <ThresholdBar
                    probability={saverPlate.share}
                    threshold={saverPlate.threshold}
                    barClassName="bg-muted-foreground/45"
                  />
                )}
                <div className="text-[11px] text-muted-foreground">
                  {t("detail.panel.plateRule", {
                    state: saverPlate.rule_enabled ? t("detail.panel.plateRuleOn") : t("detail.panel.plateRuleOff"),
                  })}
                  {typeof saver.detected === "boolean" &&
                    ` · ${t("detail.panel.saverDetected", {
                      value: saver.detected ? t("detail.panel.yes") : t("detail.panel.no"),
                    })}`}
                </div>
              </div>
            )}
          </CheckRow>
        )}

        {!shortCircuited && slicing && (
          <CheckRow
            {...row("slicing", "info")}
            title={t("slicing.tab")}
            icon={<Scissors className="h-3.5 w-3.5" />}
            summary={
              slicing.status === "sliced" ? (
                <>
                  {t("slicing.sliceCount", { count: slicing.slice_count ?? 0 })}
                  {slicingSmall > 0 && (
                    <span className="text-amber-300/90">
                      {" · "}
                      {t("slicing.smallShort", { count: slicingSmall })}
                    </span>
                  )}
                </>
              ) : slicing.status === "uncut" ? (
                t("slicing.uncut")
              ) : (
                t("slicing.skipped")
              )
            }
            onActivate={onOpenTab ? () => onOpenTab("slicing") : undefined}
          />
        )}

        {isDeveloperMode && (meta.confidence != null || meta.component_count != null) && (
          <CheckRow
            {...row("model", "info")}
            title={t("detail.panel.model")}
            icon={<span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60" />}
            summary={meta.classification ?? t("common.notAvailable")}
          >
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <dt className="text-muted-foreground">{t("detail.panel.classification")}</dt>
              <dd className="truncate">{meta.classification ?? t("common.notAvailable")}</dd>
              {meta.confidence != null && (
                <>
                  <dt className="text-muted-foreground">{t("detail.panel.confidence")}</dt>
                  <dd className="tabular-nums">{pct(meta.confidence)}</dd>
                </>
              )}
              {meta.component_count != null && (
                <>
                  <dt className="text-muted-foreground">{t("detail.panel.components")}</dt>
                  <dd className="tabular-nums">{meta.component_count}</dd>
                </>
              )}
            </dl>
          </CheckRow>
        )}
      </div>
    </section>
  );
}

/** Evaluations made before the crust-strip CNN: the verdict with its confidence, no probability bar. */
function LegacyDonenessRow({
  label,
  positive,
  confidence,
}: {
  label: string;
  positive: boolean;
  confidence?: number;
}) {
  const { t, formatNumber } = useLocalization();
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className={positive ? "font-medium" : "text-foreground/80"}>{label}</span>
      <span className={cn("text-xs tabular-nums", positive ? "font-semibold text-red-300" : "text-muted-foreground")}>
        {positive ? t("status.nok") : t("status.ok")}
        {typeof confidence === "number" &&
          ` · ${formatNumber(confidence, { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 })}`}
      </span>
    </div>
  );
}
